// ./src/utils/api/EventFileService.ts
//
// Files belonging to one event. The blobs live in ms_storage; eventsmagic-api owns the
// authorization and addresses them by `contextPath` (`e/{eventId}`, and deeper for a
// page or widget), so there is no event-file table and nothing to create here.
//
// READS, metadata edits AND uploads all go through eventsmagic-api's proxy (it proves the
// caller owns the event OR is authorized crew, and is where guest/crew visibility rules
// land). `upload` POSTs the multipart to eventsmagic-api's `/api/v1/event-files` so the
// blob is scoped to the EVENT's account with the interservice key — the only way a
// cross-account or non-account crew member's upload shows up on the event's shelf.

import { gql } from "@apollo/client";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { isRequestTimeout, notifyWriteTimeout, postFormWithSessionRetry, requestTimeoutMessage, UPLOAD_RESPONSE_MS, UPLOAD_STALL_MS } from "@em/api";

import { appEnv } from "../../config/branding";

import { ApiService } from "./ApiService";
import { CrewSessionService } from "./CrewSessionService";
import { Pagination, SortItem } from "./commonApiTypes";
import { UploadedFile } from "./UploadedFileService";

// =============================================================================
// GraphQL
// =============================================================================

const EVENT_FILE_FIELDS = `
  id
  accountId
  userId
  eventId
  contextPath
  purpose
  kind
  name
  originalFilename
  fileUrl
  thumbnailUrl
  mimeType
  sizeBytes
  notes
  orderNo
  uploadedByKind
  uploadedById
  uploadedByName
  status
  createdAt
  updatedAt
  rights
`;

const EVENT_FILE_LIST_QUERY = gql`
  query EventFileList($filter: EventFileFilter, $params: PaginationAndSorting) {
    eventFileList(filter: $filter, params: $params) {
      code
      data {
        ${EVENT_FILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_FILE_SUMMARY_QUERY = gql`
  query EventFileSummary($filter: EventFileFilter) {
    eventFileSummary(filter: $filter) {
      code
      data {
        totalFiles
        totalBytes
        truncated
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_FILE_UPDATE_MUTATION = gql`
  mutation EventFileUpdate($where: EventFileWhereInput, $params: EventFileInput) {
    eventFileUpdate(where: $where, params: $params) {
      code
      data {
        ${EVENT_FILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// A2e #99 — keep unsent guest pictures (pending → one of the event's files).
const EVENT_FILE_ACTIVATE_MUTATION = gql`
  mutation EventFileActivate($params: EventFileActivateInput) {
    eventFileActivate(params: $params) {
      code
      data {
        ${EVENT_FILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_FILE_REMOVE_MUTATION = gql`
  mutation EventFileRemove($where: EventFileWhereInput) {
    eventFileRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/**
 * Structurally a stored file plus the event it belongs to, so every component built
 * for the Storage library (grid, card, preview, edit drawer) works on it unchanged.
 */
export interface EventFile extends UploadedFile {
  eventId?: string | null;
  updatedAt?: string | null;
  rights?: string[] | null;
}

export interface EventFileFilter {
  /** Required — every query is scoped to one event. */
  eventId: string;
  /** ROADMAP #130 — these files only: a stage reopened on a reload reads its file BY ID (still within the event). */
  id?: string[];
  /** Exact context path(s); omit for the event's whole subtree. */
  contextPath?: string[];
  purpose?: string[];
  kind?: string[];
  uploadedByKind?: string[];
  searchKeyword?: string;
  /**
   * A2e #99 — true: ONLY the unsent guest pictures. Omitted: the event's files (never the unsent ones).
   * ⚠ Sent only by the "Unsent pictures" view, so a gateway that does not know it yet breaks that view alone.
   */
  pending?: boolean;
}

/**
 * A2e #99 — ms_storage's PENDING status (@sdflc/utils STATUSES.PENDING), mirrored: a guest's picture from a form nobody
 * sent. Read from `status` (always selected) rather than a new field, so the ordinary list asks nothing new.
 */
export const EVENT_FILE_STATUS_PENDING = 300;

export const isPendingEventFile = (file: { status?: number | null } | null | undefined): boolean =>
  Number(file?.status) === EVENT_FILE_STATUS_PENDING;

export interface EventFileInput {
  name?: string | null;
  purpose?: string | null;
  notes?: string | null;
}

export interface EventFileUploadInput {
  /** The event this file belongs to (also derivable from contextPath). */
  eventId: string;
  file: File;
  name?: string | null;
  purpose?: string | null;
  /** Deeper address than the event root (a page/widget/accommodation gallery). Defaults to `e/{eventId}`. */
  contextPath?: string | null;
  uploadedByName?: string | null;
  uploadedByKind?: string | null;
}

export interface EventFileSummary {
  totalFiles: number;
  totalBytes: number;
  /** The event holds more files than the server walked — the totals are a floor. */
  truncated: boolean;
}

// =============================================================================
// Context paths
// =============================================================================
//
// Mirrors the server's boundary/eventFile.ts. Kept here so the uploader can tag a file
// with the same address the proxy queries by — the two must agree or an upload lands
// outside the event's subtree and silently disappears from this page.

export const eventContextPath = (eventId: string): string => `e/${eventId}`;

export const eventPageContextPath = (eventId: string, pageId: string): string =>
  `${eventContextPath(eventId)}/p/${pageId}`;

export const eventWidgetContextPath = (eventId: string, pageId: string, widgetId: string): string =>
  `${eventPageContextPath(eventId, pageId)}/w/${widgetId}`;

/** `e/{eventId}/ac/{accommodationId}` — room photos for one accommodation. */
export const eventAccommodationContextPath = (eventId: string, accommodationId: string): string =>
  `${eventContextPath(eventId)}/ac/${accommodationId}`;

/** `e/{eventId}/su/{signupItemId}` — photos (main + extras) for one signup-list item. */
export const eventSignupItemContextPath = (eventId: string, signupItemId: string): string =>
  `${eventContextPath(eventId)}/su/${signupItemId}`;

// =============================================================================
// Service
// =============================================================================

export class EventFileService {
  /** One page of the event's files. */
  static async list(
    filter: EventFileFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_FILE_LIST_QUERY, {
      filter,
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
  }

  /** Files + bytes the event holds, for the usage meter. */
  static async summary(filter: EventFileFilter): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_FILE_SUMMARY_QUERY, { filter });
  }

  /** Edits the descriptive props. Pass many ids to re-classify a selection (purpose only). */
  static async update(ids: string | string[], params: EventFileInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_FILE_UPDATE_MUTATION, {
      where: { id: Array.isArray(ids) ? ids : [ids] },
      params,
    });
  }

  /** A2e #99 — keeps unsent guest pictures: they join the event's files and are no longer removed after a month. */
  static async activate(ids: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_FILE_ACTIVATE_MUTATION, {
      params: { ids: Array.isArray(ids) ? ids : [ids] },
    });
  }

  /** Removes one file or many in a single call. */
  static async remove(ids: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_FILE_REMOVE_MUTATION, {
      where: { id: Array.isArray(ids) ? ids : [ids] },
    });
  }

  /**
   * Uploads a file to the event's shelf THROUGH eventsmagic-api (not straight to ms_storage). The API
   * authorizes the caller (owner OR crew with event.operate) and scopes the blob to the EVENT's account
   * with the interservice key — the only way a cross-account, or a non-account password, crew member's
   * upload lands on the event's shelf (a direct browser→ms_storage upload would land under the uploader's
   * own/empty account and vanish from this page). Sends the account session cookie (`credentials`) AND the
   * crew token, so all three caller kinds authenticate. See docs/CREW-ACCESS.md.
   */
  static async upload(input: EventFileUploadInput): Promise<OpResult> {
    try {
      const base = (appEnv.rootApiUrl || "").replace(/\/+$/, "");

      if (!base) {
        return OpResult.fail(OP_RESULT_CODES.FAILED, {}, "The upload endpoint is not configured");
      }

      const formData = new FormData();
      formData.append("files", input.file);
      formData.append("eventId", input.eventId);

      if (input.name) {
        formData.append("name", input.name);
      }
      if (input.purpose) {
        formData.append("purpose", input.purpose);
      }
      if (input.contextPath) {
        formData.append("contextPath", input.contextPath);
      }
      formData.append("uploadedByKind", input.uploadedByKind || "user");
      if (input.uploadedByName) {
        formData.append("uploadedByName", input.uploadedByName);
      }

      const headers: Record<string, string> = { "x-space-id": appEnv.spaceId };
      const crewToken = CrewSessionService.getToken();

      if (crewToken) {
        headers["x-crew-token"] = crewToken;
      }

      // ROADMAP #131 — abandoned when it stops making progress (a drawer locks while it uploads), never left waiting.
      const answer = await postFormWithSessionRetry({
        url: `${base}/api/v1/event-files`,
        body: formData,
        withCredentials: true,
        headers,
        stallMs: UPLOAD_STALL_MS,
        responseMs: UPLOAD_RESPONSE_MS,
      });

      let json: unknown = null;

      try {
        json = JSON.parse(answer.text);
      } catch {
        json = null;
      }

      if (json) {
        return json instanceof OpResult ? json : new OpResult(json as never);
      }

      return OpResult.fail(OP_RESULT_CODES.FAILED, {}, `Upload failed (${answer.status})`);
    } catch (error) {
      if (isRequestTimeout(error)) {
        notifyWriteTimeout();

        return OpResult.fail(OP_RESULT_CODES.NETWORK_ERROR, {}, requestTimeoutMessage(error));
      }

      return OpResult.fromException(error);
    }
  }
}
